import React from "react";
import styled from "styled-components";
import { SectionRoot, SectionSub, SectionTitle } from "../shared";
import AccountsView from "./AccountsView";
import AssetsView from "./AssetsView";
import ProjectsView from "./ProjectsView";
import DeliveriesView from "./DeliveriesView";
import CampaignsView from "./CampaignsView";
import JobsView from "./JobsView";
import TrendsView from "./TrendsView";
import HealthView from "./HealthView";
import InsightsView from "./InsightsView";

// The Delivery section: the media pipeline that replaces Fastlane. Every
// action calls the `mediaOps` callable, the same operations the MCP tools use.
// Sub-tabs sync to ?tab= so each view is deep-linkable.

export type PipelineTab = "accounts" | "assets" | "projects" | "deliveries" | "campaigns" | "jobs" | "trends" | "insights" | "health";

export const PIPELINE_TABS: { id: PipelineTab; label: string }[] = [
  { id: "accounts", label: "Accounts" },
  { id: "assets", label: "Assets" },
  { id: "projects", label: "Projects" },
  { id: "deliveries", label: "Deliveries" },
  { id: "insights", label: "Insights" },
  { id: "campaigns", label: "Campaigns" },
  { id: "jobs", label: "Jobs" },
  { id: "trends", label: "Trends" },
  { id: "health", label: "Health" },
];

export const parsePipelineTab = (value: string | null): PipelineTab =>
  PIPELINE_TABS.some((t) => t.id === value) ? (value as PipelineTab) : "deliveries";

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 2px;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const TabButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: ${(p) => (p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)")};
  color: ${(p) => (p.$active ? "var(--color-text)" : "var(--color-text-secondary)")};
  padding: 8px 12px;
  border-bottom: 2px solid ${(p) => (p.$active ? "var(--color-accent)" : "transparent")};
  cursor: pointer;
  white-space: nowrap;
  min-height: 40px;
`;

export const PipelineSection: React.FC<{
  communityId: string;
  tab: PipelineTab;
  onSelectTab: (tab: PipelineTab) => void;
}> = ({ communityId, tab, onSelectTab }) => (
  <SectionRoot>
    <div>
      <SectionTitle>Delivery</SectionTitle>
      <SectionSub>
        Choose direct publishing or an email with your approved media and caption. A post counts as published only when
        the platform verifies it or you confirm you published it.
      </SectionSub>
    </div>
    <Tabs role="tablist">
      {PIPELINE_TABS.map((t) => (
        <TabButton key={t.id} type="button" role="tab" aria-selected={tab === t.id} $active={tab === t.id} onClick={() => onSelectTab(t.id)}>
          {t.label}
        </TabButton>
      ))}
    </Tabs>
    {tab === "accounts" ? (
      <AccountsView communityId={communityId} />
    ) : tab === "assets" ? (
      <AssetsView communityId={communityId} />
    ) : tab === "projects" ? (
      <ProjectsView communityId={communityId} />
    ) : tab === "campaigns" ? (
      <CampaignsView communityId={communityId} />
    ) : tab === "jobs" ? (
      <JobsView communityId={communityId} />
    ) : tab === "trends" ? (
      <TrendsView communityId={communityId} />
    ) : tab === "insights" ? (
      <InsightsView communityId={communityId} />
    ) : tab === "health" ? (
      <HealthView communityId={communityId} />
    ) : (
      <DeliveriesView communityId={communityId} />
    )}
  </SectionRoot>
);

export default PipelineSection;
